import React, { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import type { UseModalProps } from '@trinity/chat-components/components/Modal';
import { Modal, Header } from '@trinity/chat-components/components/Modal';
import { ButtonSet } from '@trinity/chat-components/components/Modal/ButtonSet';
import Button from '@trinity/chat-components/components/presentation/Button';

export type AvatarOption = {
  id: string;
  imageUrl: string;
  label?: string;
};

type ChooseAvatarModalProps = {
  avatarOptions: AvatarOption[];
  selectedAvatarId?: string;
  onSave: (selectedAvatarId: string, imageUrl: string) => Promise<void>;
  isSaving?: boolean;
  whitelabelEnabled?: boolean;
  primaryColor?: string;
} & UseModalProps;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 8px 0;
`;

const AvatarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 100%;
`;

const AvatarCard = styled.div<{ selected: boolean; borderColor: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid ${props => props.selected ? props.borderColor : 'transparent'};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.selected ? props.borderColor : '#e0e0e0'};
    transform: scale(1.02);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const StyledButton = styled(Button)`
  min-width: 130px;
`;

export function ChooseAvatarModal({
  open,
  onClose,
  onSave,
  avatarOptions,
  selectedAvatarId,
  isSaving = false,
  whitelabelEnabled,
  primaryColor,
}: ChooseAvatarModalProps) {
  const [localSelectedId, setLocalSelectedId] = useState<string | undefined>(selectedAvatarId);

  // Compute selected border color (consistent with whitelabeling)
  const selectedBorderColor = whitelabelEnabled && primaryColor ? primaryColor : '#28aae1';

  const handleAvatarClick = useCallback((avatarId: string) => {
    setLocalSelectedId(avatarId);
  }, []);

  const handleSave = useCallback(async () => {
    if (!localSelectedId) return;
    
    const selectedAvatar = avatarOptions.find(opt => opt.id === localSelectedId);
    if (!selectedAvatar) return;

    await onSave(localSelectedId, selectedAvatar.imageUrl);
    onClose();
  }, [localSelectedId, avatarOptions, onSave, onClose]);

  const handleCancel = useCallback(() => {
    setLocalSelectedId(selectedAvatarId);
    onClose();
  }, [selectedAvatarId, onClose]);

  const Footer = useCallback(() => {
    return (
      <ButtonSet>
        <StyledButton
          disabled={isSaving}
          color="secondary"
          onClick={handleCancel}
          variant="brick">
          Cancel
        </StyledButton>
        <StyledButton
          disabled={!localSelectedId || isSaving}
          color="primary"
          onClick={handleSave}
          variant="brick">
          {isSaving ? 'Saving...' : 'Save'}
        </StyledButton>
      </ButtonSet>
    );
  }, [localSelectedId, isSaving, handleSave, handleCancel]);

  return (
    <Modal open={open} onClose={handleCancel} blocking={isSaving}>
      <Header>Choose Avatar</Header>
      <Container>
        <AvatarGrid>
          {avatarOptions.map((avatar) => (
            <AvatarCard
              key={avatar.id}
              selected={localSelectedId === avatar.id}
              borderColor={selectedBorderColor}
              onClick={() => handleAvatarClick(avatar.id)}>
              <img 
                src={avatar.imageUrl} 
                alt={avatar.label || `Avatar ${avatar.id}`}
              />
            </AvatarCard>
          ))}
        </AvatarGrid>
      </Container>
      <Footer />
    </Modal>
  );
}
